import Events from '@utilities/events';

const STEP_ACTIVE_CLASS = 'form-step--is-active';
const HIDDEN_CLASS = 'u-hidden';

const STEP_DATA_COUNT = 'data-step-count';
const STEP_DATA_CONFIRMATION = '[data-step-confirmation]';

const STEPS_CONTENT_HOOK = '[js-hook-form-steps-content]';
const STEP_HOOK = '[js-hook-form-step]';
const STEP_CURRENT_HOOK = '[js-hook-step-form-current-step]';
const STEP_TOTAL_HOOK = '[js-hook-step-form-total-steps]';
const STEP_NAV_BUTTON_HOOK = '[js-hook-step-form-nav]';
const STEP_FORM_INDICATOR = '[js-hook-step-form-indicator]';
const FORM_SUBMIT_BUTTON_HOOK = '[js-hook-step-form-submit]';
const FORM_NAME_PLACEHOLDER_HOOK = '[js-hook-form-name-placeholder]';
const CHECKBOX_REQUIRED_HOOK = '[js-hook-checkbox-required]';
const CLEAR_BUTTON_HOOK = '[js-hook-clear-selection-button]';
const ALERT_HOOK = '[js-hook-alert]';
const LAP_HOOK = '[js-hook-lap]';

class stepForm {
    constructor(element) {
        this.element = element;
        this.stepsScrollEl = this.element.querySelector(STEPS_CONTENT_HOOK);
        this.form = this.element.querySelector('form');
        this.formSteps = [...this.element.querySelectorAll(STEP_HOOK)];
        this.initialCurrentStepEl = this.element.getElementsByClassName(STEP_ACTIVE_CLASS)[0];
        this.totalSteps = [...this.element.querySelectorAll(STEP_HOOK + `:not(${STEP_DATA_CONFIRMATION})`)].length;
        this.currentStepEl = this.element.querySelector(STEP_CURRENT_HOOK);
        this.stepsIndicator = this.element.querySelector(STEP_FORM_INDICATOR);
        this.totalStepEl = this.element.querySelector(STEP_TOTAL_HOOK);
        this.stepNavButtons = [...this.element.querySelectorAll(STEP_NAV_BUTTON_HOOK)];
        this.formSubmitButton = this.element.querySelector(FORM_SUBMIT_BUTTON_HOOK);
        this.clearSelectionButtons = [...this.element.querySelectorAll(CLEAR_BUTTON_HOOK)];

        this.bindEvents();

        this.init();
    }

    /**
     * Bind all events
     */
    bindEvents() {
        this.stepNavButtons.forEach(button => {
            button.addEventListener('click', event => this.stepHandler(event));
        });

        Events.$on('step-form::focus', () => this.focusInput());
        Events.$on(`step-form[${this.element.id}]::reset`, () => this.resetForm());

        if (this.clearSelectionButtons) {
            this.clearSelectionButtons.forEach(button => {
                button.addEventListener('click', button => this.clearSelection(button));
            });
        }

        if (this.formSubmitButton) {
            const applicationSubmit = this.element.querySelector('#edit-application-submit');

            if (applicationSubmit) {
                this.formSubmitButton.addEventListener('click', () => {
                    let vacancyInformation = window.vacancyInformation || [];
                    window.dataLayer = window.dataLayer || [];

                    window.dataLayer.push({
                        event: 'application',
                        status: 'success',
                        vacancy: vacancyInformation.vacancy,
                        vacancyId: vacancyInformation.vacancyId,
                        discipline: vacancyInformation.discipline,
                        disciplineId: vacancyInformation.disciplineId,
                        experience: vacancyInformation.experience,
                        experienceId: vacancyInformation.experienceId,
                        countryCode: vacancyInformation.countryCode,
                    });
                    applicationSubmit.click();
                });
            }
        }
    }

    init() {
        //set step indicator | total steps
        this.totalStepEl.innerHTML = this.totalSteps;

        this.bindInputEvents(this.initialCurrentStepEl);

        this.formStepValidityCheck();
    }

    bindInputEvents(currentStep) {
        const inputFields = [...currentStep.querySelectorAll('input')];

        inputFields.forEach(inputField => {
            const inputIsInput = inputField.classList.contains('input__input');
            const eventListener = inputIsInput ? 'keyup' : 'change';

            inputField.addEventListener(eventListener, e => {
                this.formStepValidityCheck(e);
            });

            if (inputIsInput) {
                inputField.addEventListener('focus', e => {
                    this.formStepValidityCheck(e);
                });
            }
        });
    }

    formStepValidityCheck(e) {
        const currentStep = e ? e.currentTarget?.closest(STEP_HOOK) : this.element.getElementsByClassName(STEP_ACTIVE_CLASS)[0];
        const currentStepInputs = [...currentStep.querySelectorAll('input')];
        const nextButton = currentStep.querySelector(STEP_NAV_BUTTON_HOOK + `[data-direction="next"]`);

        this.checkInputsMaxValue(currentStep);

        if (nextButton) {
            nextButton.disabled = !this.checkInputFieldsValidity(currentStepInputs);
        }

        if (this.formSubmitButton) {
            this.formSubmitButton.disabled = !this.checkInputFieldsValidity(currentStepInputs);
        }
    }

    checkInputFieldsValidity(inputs) {
        let inputsValidity = true;

        const mandatoryCheckboxes = {};

        inputs.forEach(input => {
            if (input.classList.contains('checkbox__input') && input.closest(CHECKBOX_REQUIRED_HOOK)) {
                const normalizedFieldName = input.name.split('[')[0];
                const existingValue = mandatoryCheckboxes[normalizedFieldName];
                mandatoryCheckboxes[normalizedFieldName] = existingValue || input.checked;
            } else if (!input.validity.valid) {
                inputsValidity = false;
            }
        });

        if (!inputsValidity) return inputsValidity;

        for (const key in mandatoryCheckboxes) {
            if (!inputsValidity) break;
            inputsValidity = mandatoryCheckboxes[key];
        }

        return inputsValidity;
    }

    checkInputsMaxValue(currentStep) {
        const inputWrappersWithMaxValue = [...currentStep.querySelectorAll('.form__item[data-max]')];

        if (!inputWrappersWithMaxValue) return;

        inputWrappersWithMaxValue.forEach(inputWrapper => {
            const maxValue = parseInt(inputWrapper.dataset.max);
            let totalCheckedItems = 0;

            const inputs = [...inputWrapper.querySelectorAll('input')];

            inputs.forEach(input => {
                if (input.checked) {
                    totalCheckedItems++;
                }
            });

            if (totalCheckedItems === maxValue) {
                inputs.forEach(input => {
                    if (!input.checked) {
                        input.setAttribute('disabled', true);
                    }
                });
            } else {
                inputs.forEach(input => {
                    input.removeAttribute('disabled');
                });
            }
        });
    }

    stepHandler(event) {
        const currentStepEl = this.element.getElementsByClassName(STEP_ACTIVE_CLASS)[0];
        const currentStepId = currentStepEl.getAttribute(STEP_DATA_COUNT);

        const nextStepId = event.currentTarget.getAttribute('data-direction') === 'next' ? parseInt(currentStepId, 10) + 1 : parseInt(currentStepId, 10) - 1;
        const nextStepEl = this.element.querySelector(`[` + STEP_DATA_COUNT + `="` + nextStepId + `"]`);
        const nextStepInput = nextStepEl.querySelector('.input__input');

        this.bindInputEvents(nextStepEl);

        //remove active class on current step
        currentStepEl.classList.remove(STEP_ACTIVE_CLASS);

        //add active class to next step
        nextStepEl.classList.add(STEP_ACTIVE_CLASS);

        //set step indicator to next step
        this.currentStepEl.innerHTML = nextStepId;

        //replace placeholder with value of name input
        const formNamePlaceholder = this.element.querySelector(FORM_NAME_PLACEHOLDER_HOOK);
        if (nextStepId === 2 && formNamePlaceholder) {
            formNamePlaceholder.innerHTML = this.element.querySelector(`input[name="name"]`).value;
        }

        //if next step has input > set focus in it
        if (nextStepInput) {
            nextStepInput.focus();
        }

        this.stepsScrollEl.scrollTop = 0;

        this.formStepValidityCheck();
    }

    focusInput() {
        const currentStepEl = this.element.getElementsByClassName(STEP_ACTIVE_CLASS)[0];
        const currentStepInput = currentStepEl.querySelector('.input__input:not([disabled])');

        if (currentStepInput) {
            currentStepInput.focus();
        }
    }

    resetForm() {
        this.form.reset();

        const messages = [...this.form.querySelectorAll(ALERT_HOOK)];

        this.formSteps.forEach(step => {
            step.classList.remove(STEP_ACTIVE_CLASS);
            this.checkInputsMaxValue(step);
            const inputFields = [...step.querySelectorAll('input')];

            inputFields.forEach(input => {
                const lapEl = input.closest(LAP_HOOK);
                if (lapEl) {
                    Events.$trigger('label-as-placeholder::reset', lapEl);
                }
            });
        });
        this.initialCurrentStepEl.classList.add(STEP_ACTIVE_CLASS);

        messages.forEach(message => {
            message.classList.add(HIDDEN_CLASS);
        });

        this.stepsIndicator.classList.remove(HIDDEN_CLASS);
        this.currentStepEl.innerHTML = '1';

        this.stepNavButtons.forEach(button => {
            if (!button.dataset.direction || button.dataset.direction === 'next') {
                button.setAttribute('disabled', 'true');
            }
        });
    }

    clearSelection(button) {
        const checkboxes = [...button.currentTarget.closest('.form__item-wrapper').querySelectorAll('.checkbox__input')];

        checkboxes.forEach(checkbox => {
            checkbox.checked = false;
        });

        this.formStepValidityCheck();
    }
}

export default stepForm;
